/**
 * Stream event router utility
 * Centralized SSE event type routing logic
 */

/**
 * Event types enum
 */
export const EVENT_TYPES = {
  START: 'start',
  AGENT_TOKEN: 'agent_token',
  COMPANIES: 'companies',
  CONTACTS: 'contacts',
  STATUS: 'status',
  CUSTOM_FIELD_DEFINITION: 'custom_field_definition',
  COMPANIES_CUSTOM_FIELD_UPDATE: 'companies_custom_field_update',
  CONTACTS_CUSTOM_FIELD_UPDATE: 'contacts_custom_field_update',
  SEC_FILINGS_CUSTOM_FIELD_UPDATE: 'sec_filings_custom_field_update',
  CONTACT_FIELD_DEFINITION: 'contact_field_definition',
  EMAIL_DRAFT_UPDATE: 'email_draft_update',
  EMAIL_BATCH_COMPLETE: 'email_batch_complete',
  CONTINUING: 'continuing',
  END: 'end',
  ERROR: 'error',
};

/**
 * Parse SSE event data
 * 
 * @param {string} eventData - Raw event data string
 * @returns {Object|null} - Parsed event object or null if parsing fails
 */
export const parseEventData = (eventData) => {
  if (!eventData) return null;
  
  try {
    return JSON.parse(eventData);
  } catch (e) {
    console.error('Error parsing SSE data:', e);
    return null;
  }
};

/**
 * Route SSE event to appropriate handler
 * 
 * @param {Object} eventData - Parsed event data
 * @param {Object} handlers - Object containing handler functions
 * @param {Function} handlers.onStart - Handler for start events
 * @param {Function} handlers.onToken - Handler for token events
 * @param {Function} handlers.onData - Handler for data events (companies, contacts, status, custom fields)
 * @param {Function} handlers.onEnd - Handler for end events
 * @param {Function} handlers.onError - Handler for error events
 * @returns {boolean} - True if event was handled, false otherwise
 */
export const routeEvent = (eventData, handlers = {}) => {
  if (!eventData || !eventData.event) return false;

  const { event, data } = eventData;

  switch (event) {
    case EVENT_TYPES.START:
      if (handlers.onStart) {
        handlers.onStart(data);
      }
      return true;

    case EVENT_TYPES.AGENT_TOKEN:
      if (handlers.onToken) {
        handlers.onToken(data);
      }
      return true;

    case EVENT_TYPES.COMPANIES:
    case EVENT_TYPES.CONTACTS:
    case EVENT_TYPES.STATUS:
    case EVENT_TYPES.CUSTOM_FIELD_DEFINITION:
    case EVENT_TYPES.COMPANIES_CUSTOM_FIELD_UPDATE:
    case EVENT_TYPES.CONTACTS_CUSTOM_FIELD_UPDATE:
    case EVENT_TYPES.SEC_FILINGS_CUSTOM_FIELD_UPDATE:
    case EVENT_TYPES.CONTACT_FIELD_DEFINITION:
    case EVENT_TYPES.EMAIL_DRAFT_UPDATE:
    case EVENT_TYPES.EMAIL_BATCH_COMPLETE:
      if (handlers.onData) {
        handlers.onData(eventData);
      }
      return true;

    case EVENT_TYPES.CONTINUING:
      // No action needed for continuing events
      return true;

    case EVENT_TYPES.END:
      if (handlers.onEnd) {
        handlers.onEnd(data);
      }
      return true;

    case EVENT_TYPES.ERROR:
      if (handlers.onError) {
        handlers.onError(data);
      }
      return true;

    default:
      // Unknown event type - pass to data handler if available
      if (handlers.onData) {
        handlers.onData(eventData);
      }
      return false;
  }
};

/**
 * Check if event is a data event (not control flow)
 * 
 * @param {string} eventType - Event type
 * @returns {boolean} - True if this is a data event
 */
export const isDataEvent = (eventType) => {
  return [
    EVENT_TYPES.COMPANIES,
    EVENT_TYPES.CONTACTS,
    EVENT_TYPES.STATUS,
    EVENT_TYPES.CUSTOM_FIELD_DEFINITION,
    EVENT_TYPES.COMPANIES_CUSTOM_FIELD_UPDATE,
    EVENT_TYPES.CONTACTS_CUSTOM_FIELD_UPDATE,
    EVENT_TYPES.SEC_FILINGS_CUSTOM_FIELD_UPDATE,
    EVENT_TYPES.CONTACT_FIELD_DEFINITION,
    EVENT_TYPES.EMAIL_DRAFT_UPDATE,
    EVENT_TYPES.EMAIL_BATCH_COMPLETE,
  ].includes(eventType);
};

/**
 * Custom field events that should be forwarded to onDataEvent (chat stream, orchestration)
 */
export const CUSTOM_FIELD_FORWARD_EVENTS = [
  EVENT_TYPES.CUSTOM_FIELD_DEFINITION,
  EVENT_TYPES.COMPANIES_CUSTOM_FIELD_UPDATE,
  EVENT_TYPES.CONTACTS_CUSTOM_FIELD_UPDATE,
  EVENT_TYPES.SEC_FILINGS_CUSTOM_FIELD_UPDATE,
];

/**
 * Check if event should be forwarded as a custom field event
 */
export const isCustomFieldForwardEvent = (eventType) =>
  CUSTOM_FIELD_FORWARD_EVENTS.includes(eventType);

/**
 * Check if event is a control flow event
 * 
 * @param {string} eventType - Event type
 * @returns {boolean} - True if this is a control flow event
 */
export const isControlEvent = (eventType) => {
  return [
    EVENT_TYPES.START,
    EVENT_TYPES.CONTINUING,
    EVENT_TYPES.END,
    EVENT_TYPES.ERROR,
  ].includes(eventType);
};

